/**
 * Client-side approximation of ADR-0037's "one-time first-login link
 * interstitial" (§UX). The server has no signal for "this session was just
 * established by a first-time SSO link" — `CallbackHandler` redirects to a
 * FIXED relative path ("/") deliberately (S-3: no open-redirect surface), and
 * neither it nor any session-recovery endpoint returns a "justLinked" bit
 * (see `client.ts`'s `getSession` doc comment for the sibling gap). Ideally
 * the server would carry that bit; failing that, this is the pragmatic
 * client-only stand-in — see this PR's notes for the flagged follow-up.
 *
 * Two independent pieces of state:
 *
 *   - The PENDING flag (sessionStorage, tab-scoped): set immediately before
 *     the SPA navigates away to `/t/{slug}/sso/login` (the real OAuth
 *     redirect), consumed the next time the authenticated app mounts. This is
 *     what scopes the interstitial to "landed here right after an SSO round
 *     trip", not "any time a session happens to be recovered."
 *   - The SEEN set (localStorage, persists across tabs/reloads on this
 *     device): so a user who signs in via SSO more than once on the SAME
 *     device only ever sees the interstitial after their first. A different
 *     device/browser, or cleared site data, sees it again — an accepted
 *     limitation of not having a real server-side signal.
 */

const PENDING_KEY = "comebye.sso.pending";
const SEEN_KEY = "comebye.sso.welcomed";

/** Call immediately before navigating the browser away to the tenant's real
 * `/t/{slug}/sso/login` route (org-slug submit or a distributed deep link) —
 * see `routes/LoginPage.tsx` / `routes/SSORedirectPage.tsx`. */
export function markSSOLoginPending(): void {
  try {
    window.sessionStorage.setItem(PENDING_KEY, "1");
  } catch {
    // Non-fatal — the interstitial simply won't show this round trip; sign-in
    // itself is unaffected.
  }
}

/** Consumes (clears) the pending flag and reports whether it was set. Single
 * use by design: call once, at authenticated-app mount. */
export function consumeSSOLoginPending(): boolean {
  try {
    const pending = window.sessionStorage.getItem(PENDING_KEY) === "1";
    window.sessionStorage.removeItem(PENDING_KEY);
    return pending;
  } catch {
    return false;
  }
}

function readSeenSet(): Set<string> {
  try {
    const raw = window.localStorage.getItem(SEEN_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed.filter((v): v is string => typeof v === "string")) : new Set();
  } catch {
    return new Set();
  }
}

export function hasShownSSOWelcome(userId: string): boolean {
  return readSeenSet().has(userId);
}

export function markSSOWelcomeShown(userId: string): void {
  try {
    const seen = readSeenSet();
    seen.add(userId);
    window.localStorage.setItem(SEEN_KEY, JSON.stringify(Array.from(seen)));
  } catch {
    // Non-fatal — worst case the interstitial shows again next time.
  }
}
